import React from 'react';
import {
  AbsoluteFill,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { FONTS } from '../brand/fonts';
import { SlideDeckPropsSchema, type SlideDeckProps, type SlideDeckSlide, type StatDevice } from './schema';

const BRAND_STYLES = {
  pvm: {
    name: 'PVM',
    background: '#0b1324',
    surface: '#f8fafc',
    panel: '#111d31',
    accent: '#34d399',
    accent2: '#60a5fa',
    text: '#f8fafc',
    darkText: '#0f172a',
    muted: '#cbd5e1',
  },
  // Owned-brand colors from hg-websites/src/lib/tenant/static-tenants.ts.
  gdm: {
    name: 'GDM', background: '#111827', surface: '#f9fafb', panel: '#1f2937',
    accent: '#E01E2D', accent2: '#2563EB', text: '#f9fafb', darkText: '#111827', muted: '#d1d5db',
  },
  ema: {
    name: 'EMA', background: '#111827', surface: '#fff7ed', panel: '#1f2937',
    accent: '#f97316', accent2: '#facc15', text: '#f9fafb', darkText: '#111827', muted: '#d1d5db',
  },
  // HD's existing proof kit uses the Heaviside Blue identity; no niche relabeling.
  hd: {
    name: 'Heaviside Digital', background: '#0b1424', surface: '#f8fafc', panel: '#1e2942',
    accent: '#30A6FF', accent2: '#93C5FD', text: '#ffffff', darkText: '#0b1424', muted: '#cdd6e6',
  },
  hai: {
    name: 'HAI',
    background: '#171717',
    surface: '#f4f4f5',
    panel: '#27272a',
    accent: '#60a5fa',
    accent2: '#f97316',
    text: '#fafafa',
    darkText: '#18181b',
    muted: '#d4d4d8',
  },
} as const;

function fitText(text: string, base: number, min: number): number {
  const longestWord = text.split(/\s+/).reduce((longest, word) => Math.max(longest, word.length), 0);
  const byLength = text.length > 150 ? base - 20 : text.length > 100 ? base - 14 : text.length > 70 ? base - 8 : base;
  const byWord = longestWord > 20 ? Math.min(byLength, base - 16) : byLength;
  return Math.max(min, byWord);
}

const textBase: React.CSSProperties = {
  fontFamily: FONTS.body,
  letterSpacing: 0,
  lineHeight: 1.08,
  margin: 0,
  overflowWrap: 'anywhere',
};

// ---- PiP-safe zone ----
// In assembled long-forms a talking-head PiP overlays the slide at lower-left (460x259 at
// x=48, y≈773, plus border). PIP_SAFE = abs x:[0,620] y:[680,1080] — a generous margin around
// it. CONTENT (headings, bullet lines, device ink, payoff/source/body, labels) must never
// enter it; texture and chrome (chapter numeral, progress hairline, background) may.
// The content <main> is inset 154/92/82, so content clears the zone by keeping either its
// left edge >= abs 620 (local 528) or its bottom <= abs 680 (local 526).
const PIP_CLEAR_LEFT = 536; // local paddingLeft that puts content left edges at abs >= 628
const PIP_DEBUG = false; // verification overlay for PiP-zone audits — must ship false

// ---- Device number formatting (renderer owns all formatting; specs carry plain numbers) ----
function formatDeviceNumber(value: number, unit: string): string {
  const rounded = Number.isInteger(value) ? value : Math.round(value * 10) / 10;
  const sign = rounded > 0 && unit !== '' ? '+' : '';
  return `${sign}${rounded}${unit}`;
}

function formatSignedPct(value: number): string {
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

// ---- Visual stat devices ----

function WaffleDevice({ device, brand }: { device: Extract<StatDevice, { type: 'waffle' }>; brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const filledCells = Math.round(Math.min(100, Math.max(0, device.pct)));
  const fillColor = device.tone === 'positive' ? style.accent : style.accent2;
  const cell = 62;
  const gap = cell * 0.14;
  const step = cell + gap;
  // Count the % up in lockstep with cells filling: last cell lands ~frame (filledCells-1)*0.6 + settle.
  const fillFrames = Math.max(1, filledCells * 0.6);
  const countProgress = interpolate(frame, [0, fillFrames + 12], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const countValue = Math.round(countProgress * filledCells);
  const numberOpacity = interpolate(frame, [4, 18], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  return (
    <div>
      <svg width={step * 10 - gap} height={step * 10 - gap} viewBox={`0 0 ${step * 10 - gap} ${step * 10 - gap}`}>
        {Array.from({ length: 100 }, (_, i) => {
          const row = Math.floor(i / 10);
          const col = i % 10;
          const isFilled = i < filledCells;
          const cellSpring = spring({ frame: frame - i * 0.6, fps, config: { damping: 14, stiffness: 120, mass: 0.6 } });
          const opacity = isFilled ? interpolate(cellSpring, [0, 1], [0, 1]) : 0.5;
          const scale = isFilled ? interpolate(cellSpring, [0, 1], [0.6, 1]) : 1;
          const size = cell * scale;
          const offset = (cell - size) / 2;
          return (
            <rect
              key={i}
              x={col * step + offset}
              y={row * step + offset}
              width={size}
              height={size}
              rx={3}
              fill={isFilled ? fillColor : style.panel}
              opacity={opacity}
            />
          );
        })}
      </svg>
      <div style={{ marginTop: 30, display: 'flex', alignItems: 'baseline', gap: 20 }}>
        <span
          style={{
            ...textBase,
            fontFamily: FONTS.heading,
            fontSize: 88,
            fontWeight: 900,
            color: fillColor,
            opacity: numberOpacity,
          }}
        >
          {countValue}%
        </span>
        <span style={{ ...textBase, fontSize: 32, fontWeight: 700, color: style.muted, maxWidth: 420 }}>
          {device.label}
        </span>
      </div>
    </div>
  );
}

function PairedBarDevice({
  device,
  brand,
}: {
  device: Extract<StatDevice, { type: 'paired-bar' }>;
  brand: keyof typeof BRAND_STYLES;
}) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const trackLength = 700;
  const barHeight = 96;
  const labelBand = 40; // room above each bar for its own category label
  // Each unit = [category label][bar]; the inter-unit gap keeps label-B clear of bar-A.
  const unitHeight = labelBand + barHeight;
  const unitGap = 44;
  const maxVal = Math.max(device.a, device.b, 1);
  const decimals = Math.min(2, Math.max(
    (device.a.toString().split('.')[1] || '').length,
    (device.b.toString().split('.')[1] || '').length,
  ));

  // Non-overshooting springs so the grow/count starts cleanly at exactly 0 on frame 0 —
  // an overshoot-below config can render a stray value (e.g. "8") on the first frame.
  const foilSpring = spring({ frame, fps, config: { damping: 200, stiffness: 110, mass: 0.9 } });
  const accentSpring = spring({ frame: frame - 6, fps, config: { damping: 200, stiffness: 110, mass: 0.9 } });

  const foilLen = interpolate(foilSpring, [0, 1], [0, (device.a / maxVal) * trackLength]);
  const accentLen = interpolate(accentSpring, [0, 1], [0, (device.b / maxVal) * trackLength]);

  // Value labels count 0 -> value driven by the SAME spring that grows each bar (so the number
  // lands as the bar tip arrives). Clamp the spring domain to [0,1] so the count can never fall
  // outside [0, value] — guarantees frame 0 shows exactly 0, never a garbage placeholder.
  // Both counts share one spring config, so a short 11-bar climbs over the same ~20 frames as
  // the long 94-bar — both numbers are visibly climbing together, the small one never snaps.
  const foilCount = interpolate(foilSpring, [0, 1], [0, device.a], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const accentCount = interpolate(accentSpring, [0, 1], [0, device.b], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const formatCount = (count: number) => decimals > 0
    ? `${count.toFixed(decimals)}${device.unit}`
    : formatDeviceNumber(Math.round(count), device.unit);

  // Foil bar (device.a = the losing/reference state) RECEDES: dimmer label; the accent
  // winner bar (device.b) gets the heavier, brighter number as the visual hero.
  const bars: Array<{ len: number; color: string; count: number; label: string; isWinner: boolean }> = [
    { len: foilLen, color: style.panel, count: foilCount, label: device.labels.a, isWinner: false },
    { len: accentLen, color: style.accent, count: accentCount, label: device.labels.b, isWinner: true },
  ];

  const totalHeight = unitHeight * 2 + unitGap;
  const baselineHeight = totalHeight - labelBand; // baseline spans only the bar zones, not the top label band

  return (
    <svg width={trackLength + 240} height={totalHeight} viewBox={`0 0 ${trackLength + 240} ${totalHeight}`}>
      {/* shared zero baseline, drawn only across the bar rows */}
      <rect x={0} y={labelBand} width={4} height={baselineHeight} fill={style.muted} />
      {bars.map((bar, index) => (
        <g key={bar.label} transform={`translate(0, ${index * (unitHeight + unitGap)})`} opacity={bar.isWinner ? 1 : 0.7}>
          {/* category label sits in the label band ABOVE its own bar, left-aligned to x=0 */}
          <text x={0} y={labelBand - 12} fontFamily={FONTS.body} fontSize={28} fontWeight={700} fill={style.muted}>
            {bar.label}
          </text>
          <rect x={0} y={labelBand} width={Math.max(2, bar.len)} height={barHeight} rx={6} fill={bar.color} />
          <text
            x={bar.len + 22}
            y={labelBand + barHeight / 2 + 12}
            fontFamily={FONTS.heading}
            fontSize={bar.isWinner ? 52 : 40}
            fontWeight={bar.isWinner ? 900 : 700}
            fill={bar.isWinner ? style.accent : style.muted}
          >
            {formatCount(bar.count)}
          </text>
        </g>
      ))}
    </svg>
  );
}

function SlopeDevice({ device, brand }: { device: Extract<StatDevice, { type: 'slope' }>; brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = 620;
  const height = 420;
  const leftX = width * 0.2;
  const rightX = width * 0.8;
  const values = [device.from, device.to];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const headroom = (max - min) * 0.12 || 1;
  const scaleY = (value: number) =>
    height - 60 - interpolate(value, [min - headroom, max + headroom], [0, height - 120], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const fromY = scaleY(device.from);
  const toY = scaleY(device.to);
  const lineLength = Math.hypot(rightX - leftX, toY - fromY);

  const dotSpring = spring({ frame, fps, config: { damping: 16, stiffness: 130, mass: 0.7 } });
  const drawProgress = interpolate(frame, [10, 30], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const dashOffset = lineLength * (1 - drawProgress);
  const rising = device.to >= device.from;
  const lineColor = rising ? style.accent : style.accent2;
  const delta = formatSignedPct(((device.to - device.from) / (Math.abs(device.from) || 1)) * 100);
  // The destination value counts from its start (from) up to (to) as the line draws to it.
  const toCount = Math.round(interpolate(drawProgress, [0, 1], [device.from, device.to]));

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <line
        x1={leftX}
        y1={fromY}
        x2={rightX}
        y2={toY}
        stroke={lineColor}
        strokeWidth={8}
        strokeLinecap="round"
        strokeDasharray={lineLength}
        strokeDashoffset={dashOffset}
      />
      <circle cx={leftX} cy={fromY} r={16 * interpolate(dotSpring, [0, 1], [0.4, 1])} fill={style.muted} />
      <circle cx={rightX} cy={toY} r={16 * interpolate(dotSpring, [0, 1], [0.4, 1])} fill={lineColor} />
      <text x={leftX - 20} y={fromY - 30} textAnchor="middle" fontFamily={FONTS.body} fontSize={24} fontWeight={600} fill={style.muted}>
        {device.labels.a}
      </text>
      <text x={leftX - 20} y={fromY + 46} textAnchor="middle" fontFamily={FONTS.heading} fontSize={38} fontWeight={800} fill={style.muted}>
        {formatDeviceNumber(device.from, device.unit)}
      </text>
      <text x={rightX + 20} y={toY - 30} textAnchor="middle" fontFamily={FONTS.body} fontSize={24} fontWeight={600} fill={style.muted}>
        {device.labels.b}
      </text>
      <text x={rightX + 20} y={toY + 46} textAnchor="middle" fontFamily={FONTS.heading} fontSize={38} fontWeight={800} fill={lineColor}>
        {formatDeviceNumber(toCount, device.unit)}
      </text>
      <text x={rightX + 20} y={toY + 92} textAnchor="middle" fontFamily={FONTS.body} fontSize={26} fontWeight={700} fill={lineColor} opacity={drawProgress}>
        {rising ? '▲' : '▼'} {delta}
      </text>
    </svg>
  );
}

function IsotypeDevice({
  device,
  brand,
}: {
  device: Extract<StatDevice, { type: 'isotype' }>;
  brand: keyof typeof BRAND_STYLES;
}) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const glyphSize = 64;
  const gap = 18;

  const renderGlyph = (fillFraction: number, key: number) => {
    const commonProps = { fill: style.accent, opacity: fillFraction };
    if (device.glyph === 'block') {
      return <rect key={key} width={glyphSize} height={glyphSize} rx={8} {...commonProps} />;
    }
    if (device.glyph === 'dot') {
      return <circle key={key} cx={glyphSize / 2} cy={glyphSize / 2} r={glyphSize / 2} {...commonProps} />;
    }
    // person: simple head + body glyph
    return (
      <g key={key} {...commonProps}>
        <circle cx={glyphSize / 2} cy={glyphSize * 0.22} r={glyphSize * 0.22} />
        <path d={`M ${glyphSize * 0.15} ${glyphSize} Q ${glyphSize * 0.15} ${glyphSize * 0.5} ${glyphSize / 2} ${glyphSize * 0.5} Q ${glyphSize * 0.85} ${glyphSize * 0.5} ${glyphSize * 0.85} ${glyphSize} Z`} />
      </g>
    );
  };

  return (
    <div>
      <svg width={(glyphSize + gap) * device.of - gap} height={glyphSize} viewBox={`0 0 ${(glyphSize + gap) * device.of - gap} ${glyphSize}`}>
        {Array.from({ length: device.of }, (_, i) => {
          const fullyFilled = i < Math.floor(device.count);
          const isFractional = i === Math.floor(device.count) && device.count % 1 !== 0;
          const fraction = fullyFilled ? 1 : isFractional ? device.count % 1 : 0;
          const cellSpring = spring({ frame: frame - i * 2, fps, config: { damping: 14, stiffness: 120, mass: 0.6 } });
          const entrance = interpolate(cellSpring, [0, 1], [0, 1]);
          return (
            <g key={i} transform={`translate(${i * (glyphSize + gap)}, 0)`} opacity={fraction > 0 ? entrance : 0.4}>
              {fraction > 0 ? (
                renderGlyph(fraction, i)
              ) : (
                <>
                  {device.glyph === 'block' && <rect width={glyphSize} height={glyphSize} rx={8} fill={style.panel} />}
                  {device.glyph === 'dot' && <circle cx={glyphSize / 2} cy={glyphSize / 2} r={glyphSize / 2} fill={style.panel} />}
                  {device.glyph === 'person' && (
                    <>
                      <circle cx={glyphSize / 2} cy={glyphSize * 0.22} r={glyphSize * 0.22} fill={style.panel} />
                      <path
                        d={`M ${glyphSize * 0.15} ${glyphSize} Q ${glyphSize * 0.15} ${glyphSize * 0.5} ${glyphSize / 2} ${glyphSize * 0.5} Q ${glyphSize * 0.85} ${glyphSize * 0.5} ${glyphSize * 0.85} ${glyphSize} Z`}
                        fill={style.panel}
                      />
                    </>
                  )}
                </>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ ...textBase, marginTop: 26, color: style.muted, fontSize: 30, fontWeight: 700, maxWidth: 620 }}>
        {device.label}
      </div>
    </div>
  );
}

function ArcDevice({ device, brand }: { device: Extract<StatDevice, { type: 'arc' }>; brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const size = 340;
  const strokeWidth = 32;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const fillColor = device.tone === 'positive' ? style.accent : style.accent2;
  const arcSpring = spring({ frame, fps, config: { damping: 20, stiffness: 90, mass: 1 } });
  const progress = interpolate(arcSpring, [0, 1], [0, device.pct / 100]);
  // % counts up in lockstep with the arc filling (same spring drives both).
  const countValue = Math.round(interpolate(arcSpring, [0, 1], [0, device.pct]));
  const numberOpacity = interpolate(frame, [6, 18], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={style.panel} strokeWidth={strokeWidth} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={fillColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          opacity: numberOpacity,
        }}
      >
        <span style={{ ...textBase, fontFamily: FONTS.heading, fontSize: 76, fontWeight: 900, color: fillColor }}>
          {countValue}%
        </span>
        <span style={{ ...textBase, fontSize: 24, fontWeight: 700, color: style.muted, textAlign: 'center', maxWidth: 240 }}>
          {device.label}
        </span>
      </div>
    </div>
  );
}

function NumberHeroTexture({
  device,
  brand,
  progress,
}: {
  device: Extract<StatDevice, { type: 'number-hero' }>;
  brand: keyof typeof BRAND_STYLES;
  progress: number;
}) {
  const style = BRAND_STYLES[brand];
  if (device.texture === 'unit-grid') {
    const cell = 20;
    const gap = cell * 0.14;
    const step = cell + gap;
    // Cells illuminate row-major in sync with the SAME spring driving the count-up: for a
    // percent value the lit share tracks the counted number (count at 89 → ~89 cells bright);
    // for non-percent figures the whole grid brightens with progress. Max cell opacity stays
    // low (0.12) so the grid remains a watermark — evidence, not decoration.
    const targetCells = device.suffix === '%' ? Math.min(100, Math.max(0, Math.round(Math.abs(device.value)))) : 100;
    const litCells = Math.round(Math.min(1, Math.max(0, progress)) * targetCells);
    return (
      <svg
        width={step * 10 - gap}
        height={step * 10 - gap}
        viewBox={`0 0 ${step * 10 - gap} ${step * 10 - gap}`}
        style={{ position: 'absolute', top: -30, left: -30 }}
      >
        {Array.from({ length: 100 }, (_, i) => {
          const row = Math.floor(i / 10);
          const col = i % 10;
          return (
            <rect
              key={i}
              x={col * step}
              y={row * step}
              width={cell}
              height={cell}
              rx={2}
              fill={style.surface}
              opacity={i < litCells ? 0.12 : 0.045}
            />
          );
        })}
      </svg>
    );
  }
  if (device.texture === 'sparkline' && device.sparkline) {
    const points = device.sparkline;
    const width = 360;
    const height = 90;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const path = points
      .map((point, i) => {
        const x = (i / (points.length - 1)) * width;
        const y = height - ((point - min) / range) * height;
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ position: 'absolute', bottom: -10, left: 0, opacity: 0.35 }}
      >
        <path d={path} fill="none" stroke={style.accent} strokeWidth={4} strokeLinecap="round" />
      </svg>
    );
  }
  if (device.texture === 'bracket') {
    return (
      <div
        style={{
          position: 'absolute',
          top: -20,
          left: -34,
          width: 26,
          height: 'calc(100% + 40px)',
          borderLeft: `10px solid ${style.accent}`,
          borderTop: `10px solid ${style.accent}`,
          opacity: 0.9,
        }}
      />
    );
  }
  // rule
  return (
    <div
      style={{
        position: 'absolute',
        bottom: -22,
        left: 0,
        width: '70%',
        height: 12,
        backgroundColor: style.accent,
      }}
    />
  );
}

function NumberHeroDevice({
  device,
  brand,
}: {
  device: Extract<StatDevice, { type: 'number-hero' }>;
  brand: keyof typeof BRAND_STYLES;
}) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const countSpring = spring({ frame, fps, config: { damping: 20, stiffness: 90, mass: 1 } });
  const animatedValue = interpolate(countSpring, [0, 1], [0, device.value]);
  const displayValue = animatedValue.toFixed(device.decimals);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <NumberHeroTexture device={device} brand={brand} progress={countSpring} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 6 }}>
        {device.prefix ? (
          <span style={{ ...textBase, fontFamily: FONTS.heading, fontSize: 130, fontWeight: 900, color: style.accent }}>
            {device.prefix}
          </span>
        ) : null}
        <span
          style={{
            ...textBase,
            fontFamily: FONTS.heading,
            fontSize: 340,
            fontWeight: 900,
            letterSpacing: -6,
            color: style.text,
          }}
        >
          {displayValue}
        </span>
        {device.suffix ? (
          <span style={{ ...textBase, fontFamily: FONTS.heading, fontSize: 153, fontWeight: 900, color: style.accent }}>
            {device.suffix}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ScissorDevice({ device, brand }: { device: Extract<StatDevice, { type: 'scissor' }>; brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  // Hero-first sequence: the PRIMARY up-blade (+more leads) draws and its number lands FIRST,
  // then the supporting down-blade starts ~13 frames later — so the hero beat resolves before
  // the second metric begins, rather than both animating at once.
  const upSpring = spring({ frame, fps, config: { damping: 16, stiffness: 100, mass: 0.9 } });
  const downSpring = spring({ frame: frame - 13, fps, config: { damping: 16, stiffness: 100, mass: 0.9 } });

  // Both blades share ONE origin on the LEFT edge and fan RIGHTWARD — one rising,
  // one falling — so the growing angle between them reads as scissor blades opening.
  const width = 760;
  const height = 480;
  const originX = 150;
  const originY = height / 2;
  const bladeRun = 470; // horizontal reach of a full-length blade
  const bladeRise = 168; // vertical spread of a full-length blade

  // Blade LENGTH is proportional to each value's magnitude on one shared scale, so the
  // bigger metric gets the longer, more dramatic blade — the fork dramatizes the true ratio.
  // Floor at 35% of full length so a small value's tip label stays clear of the origin.
  const maxMag = Math.max(Math.abs(device.up.value), Math.abs(device.down.value), 1);
  const upFrac = Math.max(0.35, Math.abs(device.up.value) / maxMag);
  const downFrac = Math.max(0.35, Math.abs(device.down.value) / maxMag);

  const upEndX = originX + bladeRun * upFrac * interpolate(upSpring, [0, 1], [0, 1]);
  const upEndY = originY - bladeRise * upFrac * interpolate(upSpring, [0, 1], [0, 1]);
  const downEndX = originX + bladeRun * downFrac * interpolate(downSpring, [0, 1], [0, 1]);
  const downEndY = originY + bladeRise * downFrac * interpolate(downSpring, [0, 1], [0, 1]);

  const labelOpacity = (s: number) => interpolate(s, [0.5, 1], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  // Both metrics are WINS, so neither blade is dimmed. Instead the PRIMARY (up / more leads)
  // is the hero — heavier stroke + larger number — and the down blade stays full-color but
  // visually secondary (thinner stroke, smaller label). One clear focal hero, one supporting.
  const upStroke = 18;
  const downStroke = 14;

  // Each value counts 0 -> target driven by its OWN blade spring, so the number lands as the
  // blade tip arrives. formatSignedPct handles the +/− sign; down is shown as a negative.
  const upCount = Math.round(interpolate(upSpring, [0, 1], [0, device.up.value]));
  const downCount = Math.round(interpolate(downSpring, [0, 1], [0, -Math.abs(device.down.value)]));

  return (
    <div style={{ position: 'relative', width, height }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        {/* rising blade: up-and-right (accent) — heavier, the hero */}
        <line x1={originX} y1={originY} x2={upEndX} y2={upEndY} stroke={style.accent} strokeWidth={upStroke} strokeLinecap="round" />
        <circle cx={upEndX} cy={upEndY} r={18} fill={style.accent} />
        {/* falling blade: down-and-right (accent2) — full-color but thinner, supporting */}
        <line x1={originX} y1={originY} x2={downEndX} y2={downEndY} stroke={style.accent2} strokeWidth={downStroke} strokeLinecap="round" />
        <circle cx={downEndX} cy={downEndY} r={15} fill={style.accent2} />
        {/* shared origin pivot on the left edge */}
        <circle cx={originX} cy={originY} r={12} fill={style.muted} />
      </svg>

      {/* rising value ABOVE the blade tip — HERO: poster-scale number, right-aligned so its
          right edge rides the tip and never reaches into the support column at 130px. */}
      <div
        style={{
          position: 'absolute',
          top: upEndY - 196,
          left: upEndX - 250,
          width: 340,
          textAlign: 'right',
          opacity: labelOpacity(upSpring),
        }}
      >
        <div style={{ ...textBase, fontFamily: FONTS.heading, fontSize: 130, fontWeight: 900, lineHeight: 1, color: style.accent }}>
          {formatSignedPct(upCount)}
          {device.up.unit && !device.up.unit.includes('%') ? device.up.unit : ''}
        </div>
        <div style={{ ...textBase, marginTop: 10, fontSize: 34, fontWeight: 700, color: style.muted }}>{device.up.label}</div>
      </div>

      {/* falling value at the bottom-right blade tip — SUPPORTING: smaller */}
      <div
        style={{
          position: 'absolute',
          top: downEndY + 24,
          left: downEndX + 20,
          width: 320,
          opacity: labelOpacity(downSpring),
        }}
      >
        <div style={{ ...textBase, fontFamily: FONTS.heading, fontSize: 72, fontWeight: 900, lineHeight: 1, color: style.accent2 }}>
          {formatSignedPct(downCount)}
          {device.down.unit && !device.down.unit.includes('%') ? device.down.unit : ''}
        </div>
        <div style={{ ...textBase, marginTop: 8, fontSize: 26, fontWeight: 700, color: style.muted }}>{device.down.label}</div>
      </div>

      {/* shared anchor label at the origin, clear of both strokes (sits to the left of the pivot) */}
      <div
        style={{
          position: 'absolute',
          top: originY - 24,
          left: 0,
          width: originX - 26,
          textAlign: 'right',
          ...textBase,
          fontSize: 24,
          fontWeight: 800,
          textTransform: 'uppercase',
          color: style.muted,
        }}
      >
        {device.anchor}
      </div>

      {/* V2: device footnote intentionally not rendered (kill list: footnotes). The schema
          field remains for spec compatibility. */}
    </div>
  );
}

function StatDeviceView({ device, brand }: { device: StatDevice; brand: keyof typeof BRAND_STYLES }) {
  if (device.type === 'waffle') return <WaffleDevice device={device} brand={brand} />;
  if (device.type === 'paired-bar') return <PairedBarDevice device={device} brand={brand} />;
  if (device.type === 'slope') return <SlopeDevice device={device} brand={brand} />;
  if (device.type === 'isotype') return <IsotypeDevice device={device} brand={brand} />;
  if (device.type === 'arc') return <ArcDevice device={device} brand={brand} />;
  if (device.type === 'number-hero') return <NumberHeroDevice device={device} brand={brand} />;
  return <ScissorDevice device={device} brand={brand} />;
}

// V2 chrome: ONLY a small dim brand chip top-left (~40% of the old header's visual weight).
// The episode theme and the trigger+counter (internal jargon) never render on screen.
function Header({ brand }: { brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  return (
    <div style={{ position: 'absolute', top: 56, left: 72, zIndex: 4 }}>
      <span
        style={{
          ...textBase,
          display: 'inline-block',
          color: style.muted,
          backgroundColor: style.panel,
          borderRadius: 4,
          padding: '6px 12px',
          fontSize: 20,
          fontWeight: 800,
          textTransform: 'uppercase',
          opacity: 0.9,
        }}
      >
        {style.name}
      </span>
    </div>
  );
}

function DeckBackground({
  brand,
  slideNumber,
  totalSlides,
}: {
  brand: keyof typeof BRAND_STYLES;
  slideNumber: number;
  totalSlides: number;
}) {
  const style = BRAND_STYLES[brand];
  return (
    <AbsoluteFill style={{ backgroundColor: style.background }}>
      <div
        style={{
          position: 'absolute',
          inset: '0 0 auto 0',
          height: 18,
          backgroundColor: style.accent,
        }}
      />
      {/* Deliberate deck-progress hairline flush across the bottom edge — dim full-width
          track, accent2 fill sized to progress. Reads as intentional chrome, never cropped. */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 6,
          backgroundColor: style.panel,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${Math.min(100, (slideNumber / Math.max(1, totalSlides)) * 100)}%`,
          height: 6,
          backgroundColor: style.accent2,
        }}
      />
    </AbsoluteFill>
  );
}

function SlideShell({
  brand,
  slideNumber,
  totalSlides,
  children,
}: {
  brand: keyof typeof BRAND_STYLES;
  slideNumber: number;
  totalSlides: number;
  children: React.ReactNode;
}) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const entrance = spring({ frame, fps, config: { damping: 18, stiffness: 90, mass: 0.9 } });
  const opacity = interpolate(frame, [0, 12], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const translateY = interpolate(entrance, [0, 1], [28, 0]);

  // V2: speaker notes are internal — they never render on screen.
  return (
    <AbsoluteFill style={{ color: BRAND_STYLES[brand].text }}>
      <DeckBackground brand={brand} slideNumber={slideNumber} totalSlides={totalSlides} />
      <Header brand={brand} />
      <main
        style={{
          position: 'absolute',
          inset: '154px 92px 82px',
          opacity,
          transform: `translateY(${translateY}px)`,
        }}
      >
        {children}
      </main>
      {PIP_DEBUG ? (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 680,
            width: 620,
            height: 400,
            backgroundColor: 'rgba(255, 0, 0, 0.22)',
            border: '2px solid rgba(255, 0, 0, 0.8)',
            zIndex: 99,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </AbsoluteFill>
  );
}

// V2 TITLE: the heading IS the slide — poster scale, golden-left, vertically centered.
// The only other mark is the chapter numeral, larger and dimmer (pure texture behind).
function TitleSlide({
  slide,
  brand,
  slideNumber,
}: {
  slide: Extract<SlideDeckSlide, { kind: 'title' }>;
  brand: keyof typeof BRAND_STYLES;
  slideNumber: number;
}) {
  const style = BRAND_STYLES[brand];
  return (
    // paddingBottom lifts the centered heading so its last line clears the PiP-safe zone
    // (bottom above abs y=680). The numeral is texture — allowed inside the zone.
    <div style={{ height: '100%', position: 'relative', display: 'flex', alignItems: 'center', paddingBottom: 330 }}>
      <div
        style={{
          ...textBase,
          position: 'absolute',
          right: 0,
          bottom: 0,
          fontFamily: FONTS.heading,
          fontSize: 440,
          fontWeight: 900,
          lineHeight: 0.8,
          color: style.accent,
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        {String(slideNumber).padStart(2, '0')}
      </div>
      <h1
        style={{
          ...textBase,
          position: 'relative',
          zIndex: 1,
          color: style.text,
          fontFamily: FONTS.heading,
          fontSize: fitText(slide.heading, 120, 76),
          fontWeight: 800,
          maxWidth: 1180,
          lineHeight: 1.04,
        }}
      >
        {slide.heading}
      </h1>
    </div>
  );
}

// V2 BIG-STAT: the device is the ONLY object — centered and scaled to fill the frame's height
// budget. The payoff is one muted line beneath; source (proof credibility) one small line under
// that. No second column, no support card. Per-type scale: a uniform 1.6x is geometrically
// impossible (waffle is ~820px tall natively in an 844px content area), so each device scales
// to its own maximum within the hero zone.
const DEVICE_HERO_SCALE: Record<StatDevice['type'], number> = {
  waffle: 0.78,
  'paired-bar': 1.6,
  slope: 1.2,
  isotype: 1.8,
  arc: 1.7,
  'number-hero': 1.35,
  scissor: 1.08,
};

// PiP-safe device placement: wide devices whose scaled ink can reach the lower-left zone get
// an up-bias (device centers in the band above abs y=680). Exempt: waffle and arc (their
// boxes sit entirely right of abs x=620 when centered) and scissor (its blades fan RIGHT from
// a mid-height left origin, so its lower-left corner is structurally empty).
const DEVICE_PIP_BOTTOM_PAD: Record<StatDevice['type'], number> = {
  waffle: 0,
  'paired-bar': 208,
  slope: 208,
  isotype: 208,
  arc: 0,
  'number-hero': 208,
  scissor: 0,
};

function BigStatSlide({ slide, brand }: { slide: Extract<SlideDeckSlide, { kind: 'big-stat' }>; brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  // The payoff fades in AFTER the device has substantially drawn — eye lands device-first.
  const supportOpacity = interpolate(frame, [20, 34], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        style={{
          flex: '1 1 0',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: slide.device ? DEVICE_PIP_BOTTOM_PAD[slide.device.type] : 208,
        }}
      >
        {slide.device ? (
          <div style={{ transform: `scale(${DEVICE_HERO_SCALE[slide.device.type]})` }}>
            <StatDeviceView device={slide.device} brand={brand} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ ...textBase, color: style.accent, fontFamily: FONTS.heading, fontSize: fitText(slide.stat, 220, 130), fontWeight: 900 }}>
              {slide.stat}
            </div>
            {slide.statLabel ? <div style={{ ...textBase, color: style.text, fontSize: 55, fontWeight: 850, letterSpacing: 3 }}>{slide.statLabel}</div> : null}
          </div>
        )}
      </div>
      {/* payoff + source live low in the frame, so they center within the area RIGHT of the
          PiP-safe zone (left edges >= abs 628) instead of the full width */}
      <p
        style={{
          ...textBase,
          alignSelf: 'stretch',
          paddingLeft: PIP_CLEAR_LEFT,
          color: style.muted,
          // single-line payoff: shrink by character budget so complete words always fit one line
          fontSize: Math.min(38, Math.max(24, Math.floor(1100 / (slide.body.length * 0.52)))),
          fontWeight: 700,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          opacity: supportOpacity,
        }}
      >
        {slide.body}
      </p>
      {slide.source ? (
        <p
          style={{
            ...textBase,
            alignSelf: 'stretch',
            paddingLeft: PIP_CLEAR_LEFT,
            marginTop: 14,
            color: style.muted,
            fontSize: 22,
            fontWeight: 600,
            textAlign: 'center',
            opacity: supportOpacity * 0.7,
          }}
        >
          Source: {slide.source}
        </p>
      ) : null}
    </div>
  );
}

function StaggeredRow({
  index,
  children,
  style: rowStyle,
  emphasize = false,
}: {
  index: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
  emphasize?: boolean;
}) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const startFrame = index * 4;
  // Row i starts its spring at frame i*4 so items enter one after another, not as a block.
  const entrance = spring({ frame: frame - startFrame, fps, config: { damping: 18, stiffness: 110, mass: 0.8 } });
  const opacity = interpolate(entrance, [0, 1], [0, 1]);
  const translateX = interpolate(entrance, [0, 1], [-24, 0]);
  // Payoff row lands with ONE brief 1.0 -> 1.06 -> 1.0 pulse as it settles (~start + 12 frames),
  // so the final entrance beat is also the emphasis beat. Fires once, only for the flagged row.
  const pulse = emphasize
    ? interpolate(frame, [startFrame + 8, startFrame + 14, startFrame + 22], [1, 1.06, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      })
    : 1;
  return (
    <div style={{ ...rowStyle, opacity, transform: `translateX(${translateX}px) scale(${pulse})` }}>{children}</div>
  );
}

// V2 BULLETS: one centered column of huge bare numbered lines — accent number + text, no
// cards, no boxes, no split. Heading above, quieter than the lines. Stagger + payoff pulse
// kept. The RECAP kicker is the only kicker that renders (small and dim); PATTERN etc. die.
function BulletsSlide({
  slide,
  brand,
}: {
  slide: Extract<SlideDeckSlide, { kind: 'bullets' }>;
  brand: keyof typeof BRAND_STYLES;
}) {
  const style = BRAND_STYLES[brand];
  const isRecap = (slide.kicker ?? '').toUpperCase() === 'RECAP';
  const lastIndex = slide.body.length - 1;

  // One shared line size for ALL rows, sized by character budget against the PiP-clear
  // column (~1100px right of the safe zone) so every row is a single line of complete words.
  const longestChars = slide.body.reduce((max, item) => Math.max(max, item.length), 0);
  const lineFontSize = Math.min(52, Math.max(34, Math.floor(1100 / (longestChars * 0.52))));

  const lines = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 36, alignItems: 'flex-start' }}>
      {slide.body.map((item, index) => (
        <StaggeredRow
          key={`${index}-${item}`}
          index={index}
          emphasize={index === lastIndex}
          style={{ display: 'flex', alignItems: 'baseline', gap: 30 }}
        >
          <span
            style={{
              ...textBase,
              color: style.accent,
              fontFamily: FONTS.heading,
              fontSize: lineFontSize,
              fontWeight: 900,
              flex: '0 0 auto',
            }}
          >
            {index + 1}
          </span>
          <span style={{ ...textBase, color: style.text, fontSize: lineFontSize, fontWeight: 800, whiteSpace: 'nowrap' }}>
            {item}
          </span>
        </StaggeredRow>
      ))}
    </div>
  );

  if (slide.device) {
    // Rare bullets+device shape: device is the centered hero, lines compact beneath.
    // The bottom line row centers within the PiP-clear area (left edges >= abs 628).
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 36 }}>
        <h2 style={{ ...textBase, color: style.text, fontFamily: FONTS.heading, fontSize: fitText(slide.heading, 48, 38), fontWeight: 800, textAlign: 'center' }}>
          {slide.heading}
        </h2>
        <div
          style={{
            flex: '1 1 0',
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBottom: DEVICE_PIP_BOTTOM_PAD[slide.device.type],
          }}
        >
          <StatDeviceView device={slide.device} brand={brand} />
        </div>
        <div style={{ alignSelf: 'stretch', paddingLeft: PIP_CLEAR_LEFT, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px 40px' }}>
          {slide.body.map((item, index) => (
            <span key={`${index}-${item}`} style={{ ...textBase, color: style.muted, fontSize: 26, fontWeight: 700 }}>
              {index + 1}. {item}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // The group is anchored so its left edge starts right of the PiP-safe zone (abs x >= 652):
  // the low bullet lines can then never intersect the zone, whatever their y. The inner
  // wrapper shrink-wraps so the heading stays centered over the lines.
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', paddingLeft: 560, paddingBottom: 40 }}>
      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 54 }}>
        <div style={{ textAlign: 'center' }}>
          {isRecap ? (
            <div style={{ ...textBase, color: style.muted, fontSize: 22, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 4, opacity: 0.7, marginBottom: 18 }}>
              {slide.kicker}
            </div>
          ) : null}
          <h2 style={{ ...textBase, color: style.text, fontFamily: FONTS.heading, fontSize: fitText(slide.heading, 52, 40), fontWeight: 800, maxWidth: 1100 }}>
            {slide.heading}
          </h2>
        </div>
        {lines}
      </div>
    </div>
  );
}

// V2 BEFORE-AFTER: one horizontal split. Each side is the label text ONLY at large scale —
// before muted, after accent (subtly brighter) — separated by a thin vertical divider.
// Heading above, smaller. No panels, no boxes, no abstract dot-scale.
function BeforeAfterSlide({
  slide,
  brand,
}: {
  slide: Extract<SlideDeckSlide, { kind: 'before-after' }>;
  brand: keyof typeof BRAND_STYLES;
}) {
  const style = BRAND_STYLES[brand];

  // The BEFORE (left) cell gets a bottom pad so its text block centers in the band ABOVE
  // the PiP-safe zone (bottom <= abs y=680). The AFTER cell sits right of abs x=620, so it
  // may stay fully centered.
  const split = (labelFontBase: number, beforeBottomPad: number) => (
    <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 2px 1fr', gap: 0, alignItems: 'center' }}>
      <div style={{ padding: `0 72px ${beforeBottomPad}px 12px` }}>
        <p style={{ ...textBase, color: style.muted, opacity: 0.8, fontSize: fitText(slide.body.before, labelFontBase, 40), fontWeight: 700, lineHeight: 1.18 }}>
          {slide.body.before}
        </p>
      </div>
      <div style={{ height: '62%', width: 2, backgroundColor: style.muted, opacity: 0.35, alignSelf: 'center' }} />
      <div style={{ padding: '0 12px 0 72px' }}>
        <p style={{ ...textBase, color: style.accent, fontSize: fitText(slide.body.after, labelFontBase, 40), fontWeight: 800, lineHeight: 1.18 }}>
          {slide.body.after}
        </p>
      </div>
    </div>
  );

  if (slide.device) {
    // Numeric before/after: the device carries the delta; the split labels sit compact below.
    // The labels row lives low, so it shifts right of the PiP-safe zone entirely.
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 28 }}>
        <h2 style={{ ...textBase, color: style.text, fontFamily: FONTS.heading, fontSize: fitText(slide.heading, 48, 38), fontWeight: 800, textAlign: 'center' }}>
          {slide.heading}
        </h2>
        <div
          style={{
            flex: '1 1 0',
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBottom: DEVICE_PIP_BOTTOM_PAD[slide.device.type],
          }}
        >
          <StatDeviceView device={slide.device} brand={brand} />
        </div>
        <div style={{ paddingLeft: PIP_CLEAR_LEFT }}>{split(40, 0)}</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 40 }}>
      <h2 style={{ ...textBase, color: style.text, fontFamily: FONTS.heading, fontSize: fitText(slide.heading, 52, 40), fontWeight: 800, textAlign: 'center' }}>
        {slide.heading}
      </h2>
      {split(64, 318)}
    </div>
  );
}

// Split body around the FIRST case-insensitive occurrence of the highlight phrase so the
// matched slice can render in accent ("encode the slice, dim the rest"). Null = no match.
function splitHighlight(body: string, highlight: string): [string, string, string] | null {
  const index = body.toLowerCase().indexOf(highlight.toLowerCase());
  if (index === -1) return null;
  return [body.slice(0, index), body.slice(index, index + highlight.length), body.slice(index + highlight.length)];
}

// V2 QUOTE: quote text ~20% larger, accent key-phrase highlight kept, everything else
// around it removed (source lines only survive on proof slides).
function QuoteSlide({ slide, brand }: { slide: Extract<SlideDeckSlide, { kind: 'quote' }>; brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  const parts = slide.highlight ? splitHighlight(slide.body, slide.highlight) : null;
  return (
    // paddingBottom lifts the centered quote block so its last line clears the PiP-safe zone.
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: 320 }}>
      <div style={{ maxWidth: 1440 }}>
        <div style={{ ...textBase, color: style.accent, fontFamily: FONTS.heading, fontSize: 150, fontWeight: 900, lineHeight: 0.8 }}>
          "
        </div>
        <p style={{ ...textBase, color: style.text, fontFamily: FONTS.heading, fontSize: fitText(slide.body, 91, 62), fontWeight: 850 }}>
          {parts ? (
            <>
              {parts[0]}
              <span style={{ color: style.accent }}>{parts[1]}</span>
              {parts[2]}
            </>
          ) : (
            slide.body
          )}
        </p>
      </div>
    </div>
  );
}

// V2 CTA: the keyword pill IS the slide — centered, ~30% of canvas height. Heading (small)
// above, body one muted line below. Nothing else. Entrance: heading rides the shell fade ->
// pill springs in with slight overshoot -> body fades last.
function CtaSlide({ slide, brand }: { slide: Extract<SlideDeckSlide, { kind: 'cta' }>; brand: keyof typeof BRAND_STYLES }) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  if (slide.keyword) {
    const pillSpring = spring({ frame: frame - 8, fps, config: { damping: 12, stiffness: 120, mass: 0.8 } });
    const pillScale = interpolate(pillSpring, [0, 1], [0.7, 1]);
    const pillOpacity = interpolate(pillSpring, [0, 0.4], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const bodyOpacity = interpolate(frame, [22, 36], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    // Long keywords shrink so the pill always fits the frame width.
    const keywordFontSize = Math.min(230, Math.floor(1500 / (slide.keyword.length * 0.62)));
    return (
      // paddingBottom lifts heading+pill above the PiP-safe zone; the body line sits low, so
      // it centers within the PiP-clear area (left edge >= abs 628) instead of full width.
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 52, paddingBottom: 250 }}>
        <h2 style={{ ...textBase, color: style.text, fontFamily: FONTS.heading, fontSize: fitText(slide.heading, 52, 40), fontWeight: 800, textAlign: 'center', maxWidth: 1300 }}>
          {slide.heading}
        </h2>
        <div
          style={{
            backgroundColor: style.accent,
            color: style.background,
            borderRadius: 28,
            padding: '44px 110px',
            transform: `scale(${pillScale})`,
            opacity: pillOpacity,
          }}
        >
          <span
            style={{
              ...textBase,
              fontFamily: FONTS.heading,
              fontSize: keywordFontSize,
              fontWeight: 800,
              lineHeight: 1,
              textTransform: 'uppercase',
              letterSpacing: 3,
            }}
          >
            {slide.keyword}
          </span>
        </div>
        <p
          style={{
            ...textBase,
            alignSelf: 'stretch',
            paddingLeft: PIP_CLEAR_LEFT,
            color: style.muted,
            // one line: shrink by character budget so complete words always fit
            fontSize: Math.min(34, Math.max(24, Math.floor(1100 / (slide.body.length * 0.52)))),
            fontWeight: 700,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            opacity: bodyOpacity,
          }}
        >
          {slide.body}
        </p>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 44, paddingBottom: 250 }}>
      <h2 style={{ ...textBase, color: style.accent, fontFamily: FONTS.heading, fontSize: fitText(slide.heading, 92, 62), fontWeight: 800, textAlign: 'center', maxWidth: 1400 }}>
        {slide.heading}
      </h2>
      <p style={{ ...textBase, color: style.muted, fontSize: fitText(slide.body, 42, 32), fontWeight: 700, textAlign: 'center', maxWidth: 1100 }}>
        {slide.body}
      </p>
    </div>
  );
}

function RenderSlide({
  slide,
  brand,
  slideNumber,
}: {
  slide: SlideDeckSlide;
  brand: keyof typeof BRAND_STYLES;
  slideNumber: number;
}) {
  if (slide.kind === 'title') return <TitleSlide slide={slide} brand={brand} slideNumber={slideNumber} />;
  if (slide.kind === 'big-stat') return <BigStatSlide slide={slide} brand={brand} />;
  if (slide.kind === 'bullets') return <BulletsSlide slide={slide} brand={brand} />;
  if (slide.kind === 'before-after') return <BeforeAfterSlide slide={slide} brand={brand} />;
  if (slide.kind === 'quote') return <QuoteSlide slide={slide} brand={brand} />;
  return <CtaSlide slide={slide} brand={brand} />;
}

// A Short is composed for a tall canvas, not a landscape slide letterboxed into one.
// Both formats consume the same approved SlideSpec; only the visual arrangement changes.
function PortraitSlide({
  slide,
  brand,
  slideNumber,
  totalSlides,
}: {
  slide: SlideDeckSlide;
  brand: keyof typeof BRAND_STYLES;
  slideNumber: number;
  totalSlides: number;
}) {
  const style = BRAND_STYLES[brand];
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 12], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const textStyle: React.CSSProperties = { ...textBase, color: style.text, fontFamily: FONTS.heading, fontWeight: 800 };
  const device = 'device' in slide ? slide.device : undefined;
  const deviceScale = device?.type === 'paired-bar' ? 0.94 : device?.type === 'waffle' ? 0.78 : device?.type === 'scissor' ? 0.8 : 0.9;
  const deviceView = device ? (
    <div style={{ width: '100%', height: device.type === 'waffle' ? 850 : 650, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ transform: `scale(${deviceScale})`, transformOrigin: 'center' }}>
        <StatDeviceView device={device} brand={brand} />
      </div>
    </div>
  ) : null;

  let content: React.ReactNode;
  if (slide.kind === 'title') {
    content = <h1 style={{ ...textStyle, fontSize: fitText(slide.heading, 112, 68), lineHeight: 1.02 }}>{slide.heading}</h1>;
  } else if (slide.kind === 'big-stat') {
    const mechanism = slide.mechanism?.type === 'growth-flat' ? (
      <div style={{ width: '100%', padding: '38px 32px', borderRadius: 28, background: style.panel }}>
        <svg width="100%" height="390" viewBox="0 0 850 390" role="img" aria-label={`${slide.mechanism.growthLabel} rises while ${slide.mechanism.flatLabel} stays level`}>
          <text x="20" y="46" fill={style.text} fontSize="38" fontWeight="800" fontFamily={FONTS.body}>{slide.mechanism.growthLabel}</text>
          <path d="M 32 185 L 255 166 L 470 120 L 665 62 L 770 26" fill="none" stroke={style.accent} strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M 730 18 L 780 22 L 755 68" fill="none" stroke={style.accent} strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
          <text x="20" y="280" fill={style.text} fontSize="38" fontWeight="800" fontFamily={FONTS.body}>{slide.mechanism.flatLabel}</text>
          <path d="M 32 340 L 770 340" fill="none" stroke={style.muted} strokeWidth="13" strokeLinecap="round" />
          <path d="M 730 311 L 780 340 L 730 369" fill="none" stroke={style.muted} strokeWidth="13" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    ) : null;
    content = (
      <div style={{ minHeight: 1320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around', textAlign: 'center' }}>
        <div>
          <div style={{ ...textStyle, color: style.accent, fontSize: fitText(slide.stat, device ? 100 : 220, device ? 62 : 110), lineHeight: 1 }}>{slide.stat}</div>
          {slide.statLabel ? <div style={{ ...textStyle, color: style.text, fontSize: 56, letterSpacing: 3, marginTop: 20 }}>{slide.statLabel}</div> : null}
        </div>
        {deviceView}
        {mechanism}
        <p style={{ ...textBase, color: style.text, fontSize: fitText(slide.body, 52, 34), fontWeight: 750, lineHeight: 1.16 }}>{slide.body}</p>
        {slide.source ? <p style={{ ...textBase, color: style.muted, fontSize: 26, lineHeight: 1.2 }}>{slide.source}</p> : null}
      </div>
    );
  } else if (slide.kind === 'bullets') {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 62 }}>
        <h2 style={{ ...textStyle, fontSize: fitText(slide.heading, 82, 54) }}>{slide.heading}</h2>
        {deviceView}
        {slide.body.map((item, index) => (
          <div key={`${index}-${item}`} style={{ display: 'flex', gap: 28, alignItems: 'baseline' }}>
            <span style={{ ...textStyle, color: style.accent, fontSize: 58 }}>{index + 1}</span>
            <span style={{ ...textBase, color: style.text, fontSize: fitText(item, 54, 38), fontWeight: 750 }}>{item}</span>
          </div>
        ))}
      </div>
    );
  } else if (slide.kind === 'before-after') {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <h2 style={{ ...textStyle, fontSize: fitText(slide.heading, 78, 52), textAlign: 'center' }}>{slide.heading}</h2>
        {deviceView}
        <div style={{ borderLeft: `8px solid ${style.muted}`, paddingLeft: 32, color: style.muted, fontSize: 44, lineHeight: 1.15 }}>{slide.body.before}</div>
        <div style={{ borderLeft: `8px solid ${style.accent}`, paddingLeft: 32, color: style.text, fontSize: 48, fontWeight: 800, lineHeight: 1.15 }}>{slide.body.after}</div>
      </div>
    );
  } else if (slide.kind === 'quote') {
    content = (
      <div>
        <div style={{ ...textStyle, color: style.accent, fontSize: 160, lineHeight: 0.7 }}>&ldquo;</div>
        <p style={{ ...textStyle, fontSize: fitText(slide.body, 82, 48), lineHeight: 1.1 }}>{slide.body}</p>
        {slide.source ? <p style={{ ...textBase, color: style.muted, fontSize: 30, marginTop: 54 }}>{slide.source}</p> : null}
      </div>
    );
  } else {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 62, textAlign: 'center' }}>
        <h2 style={{ ...textStyle, fontSize: fitText(slide.heading, 92, 54) }}>{slide.heading}</h2>
        {slide.keyword ? <div style={{ ...textStyle, background: style.accent, color: style.background, borderRadius: 24, padding: '28px 52px', fontSize: fitText(slide.keyword, 110, 60) }}>{slide.keyword}</div> : null}
        <p style={{ ...textBase, color: style.muted, fontSize: fitText(slide.body, 50, 34), lineHeight: 1.15 }}>{slide.body}</p>
      </div>
    );
  }

  return (
    // Text without its own font (brand label, before/after copy, slide counter) inherits the deck's
    // body font instead of the browser's default serif.
    <AbsoluteFill style={{ backgroundColor: style.background, color: style.text, fontFamily: FONTS.body }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 18, background: style.accent }} />
      <div style={{ position: 'absolute', top: 90, left: 72, color: style.muted, fontSize: 28, fontWeight: 850, letterSpacing: 3 }}>{style.name}</div>
      <div style={{ position: 'absolute', inset: '250px 72px 250px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity }}>
        <div style={{ width: '100%' }}>{content}</div>
      </div>
      <div style={{ position: 'absolute', bottom: 100, right: 72, color: style.muted, fontSize: 24 }}>{slideNumber} / {totalSlides}</div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${(slideNumber / totalSlides) * 100}%`, height: 8, background: style.accent2 }} />
    </AbsoluteFill>
  );
}

export const SlideDeck: React.FC<SlideDeckProps> = (input) => {
  const props = SlideDeckPropsSchema.parse(input);
  const { spec, sceneSeconds, aspectRatio } = props;
  const { fps } = useVideoConfig();
  const framesPerSlide = Math.max(1, Math.round(sceneSeconds * fps));

  return (
    <AbsoluteFill style={{ backgroundColor: BRAND_STYLES[spec.brand].background }}>
      {spec.slides.map((slide, index) => (
        <Sequence key={`${index}-${slide.kind}`} from={index * framesPerSlide} durationInFrames={framesPerSlide}>
          {aspectRatio === '9:16' ? (
            <PortraitSlide slide={slide} brand={spec.brand} slideNumber={index + 1} totalSlides={spec.slides.length} />
          ) : (
            <SlideShell brand={spec.brand} slideNumber={index + 1} totalSlides={spec.slides.length}>
              <RenderSlide slide={slide} brand={spec.brand} slideNumber={index + 1} />
            </SlideShell>
          )}
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
